import { writeLiveShiftDay } from "../pages/dashboard/route-planner/helpers/live-shift-day";
import { useAuthStore } from "service";
import { toast } from "react-toastify";
import {
  useAdminRouteV2ControllerUpdateRouteLockStatus,
  useAdminRouteV2ControllerUpdateManyRouteStartTime,
  useAdminRouteV2ControllerUpdateRoutesState,
  useAdminRouteCommitStateControllerRollbackLiveRoutes,
  useAdminRouteCommitStateControllerCommitLiveRoutes,
  useAdminRouteCommitStateControllerResetRouteShiftState,
  useAdminRouteV2ControllerUpdateRouteStateLive,
  useAdminRouteV2ControllerUpdateRoute,
  useAdminRouteV2ControllerDeleteVirtualRoute,
  useAdminRouteV2ControllerUpdateRouteCustomerOrders,
  useAdminRouteV2ControllerReverseSelectedRouteLocationsSequence,
  useAdminRouteV2ControllerUpdateRouteStateEnd,
  useAdminRouteV2ControllerTransferLocationToRoute,
  useAdminRouteV2ControllerUpdateRouteDriverStatuses,
  useAdminLocationV2ControllerGetAllLocations,
  useAdminRouteV2ControllerGetRoutesPaginated,
  useAdminRouteV2ControllerGetDateOfLiveRoutes,
  useAdminRouteV2ControllerGetRouteStateDiff,
  useAdminDriverStatusControllerFindAll,
  useAdminRouteV2ControllerUpdateRoutePriority,
  useDriverControllerGetAll,
  useAdminRouteV2ControllerUpdateRouteState,
  AdminRouteV2ControllerGetRoutesStatusesPerMonthResponse,
  useAdminRouteV2ControllerGetDateOfBuildRoutes,
  useAdminRouteV2ControllerGetRouteNotificationsPerDate,
  useAdminRouteV2ControllerUpdateRouteNotificationsPerDate,
  useAdminRouteV2ControllerUpdateRouteNotifications,
  AdminRouteV2ControllerGetRouteNotificationsPerDateResponse,
  useAdminLocationV2ControllerGetUnassignedRouteLocationsForDatePaginated,
  useAdminRouteV2ControllerTriggerEstCalculation,
} from "service";
import { queryKeyFn } from "service/api/estabrookContext";
import {
  createGlobalRouteNotificationsQueryKey,
  createRoutesInvalidationPredicate,
} from "service/api/estabrookQueryKeys";
import { generateToastMessageForTransfer } from "../pages/dashboard/route-planner/build/move-locations-dialog";
import { queryClient } from "../query-client";
import { DateTime } from "luxon";
import { formatStartTimeLabel } from "../utils/date-time";
import useRoutePlannerStore from "../state/route-planner-store";
import { useEffect, useState, useMemo, useRef } from "react";
import { shallow } from "zustand/shallow";
import {
  transformRoutesToDriverMarkers,
  transformRoutesToMapMarkers,
} from "../components/map/map.utils";
import { useFilteredRouteOptions } from "../utils/route-status-options";
import { RouteStatus } from "../pages/dashboard/route-planner/helpers/route-status.enum";
import { DriverMarker, OrderStatuses } from "../components/map/map.types";

export enum AvailableStatuses {
  Skipped = "skipped",
  Regular = "regular",
  Delayed = "delayed",
  Urgent = "urgent",
}

type QueryParams = {
  includeLocations?: boolean;
  date?: string;
  orderStatuses?: ("skipped" | "regular" | "delayed" | "urgent")[];
  locationTypes?: ("onDemand" | "annual")[];
  unassignedActive?: boolean;
  unassignedInactive?: boolean;
};

type RoutePlannerApiProps = {
  date: string | undefined;
};

type OptimisticUpdateContext = {
  organizationId: string | null;
  mutationDay: string;
  queryKey: unknown[];
  previousData:
    | AdminRouteV2ControllerGetRoutesStatusesPerMonthResponse
    | undefined;
};

const parseCoordinate = (value?: string | number | null) => {
  if (value === undefined || value === null || value === "") return undefined;

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

// Helper function to update the monthly routes status query optimistically
const updateRouteStatusOptimistically = (
  date: string,
  newState: "planning" | "planned" | "live" | "ended",
): OptimisticUpdateContext => {
  const targetDate = DateTime.fromFormat(date, "yyyy-MM-dd");
  const queryKey = queryKeyFn({
    path: "/api/v2/admin/routes/states",
    operationId: "adminRouteV2ControllerGetRoutesStatusesPerMonth",
    variables: {
      queryParams: {
        month: targetDate.month,
        year: targetDate.year,
      },
    },
  });

  // Store the previous data for rollback
  const previousData =
    queryClient.getQueryData<AdminRouteV2ControllerGetRoutesStatusesPerMonthResponse>(
      queryKey,
    );

  // Update the query cache optimistically
  queryClient.setQueryData<AdminRouteV2ControllerGetRoutesStatusesPerMonthResponse>(
    queryKey,
    (oldData) => {
      if (!oldData?.data) return oldData;

      const updatedData = oldData.data.map((item) => {
        const itemDate = DateTime.fromISO(item.date);
        const targetDateOnly = targetDate.startOf("day");
        const itemDateOnly = itemDate.startOf("day");

        if (targetDateOnly.equals(itemDateOnly)) {
          return {
            ...item,
            state: newState,
          };
        }
        return item;
      });

      return {
        ...oldData,
        data: updatedData,
      };
    },
  );

  return {
    queryKey,
    previousData,
    organizationId: useAuthStore.getState().organizationId,
    mutationDay: DateTime.now().toISODate()!,
  };
};

const createRouteStateDiffQueryKey = (date: string) =>
  queryKeyFn({
    path: "/api/v2/admin/routes/date/{date}/state/check",
    operationId: "adminRouteV2ControllerGetRouteStateDiff",
    variables: {
      pathParams: { date },
    },
  });

const useRoutePlannerApi = ({ date }: RoutePlannerApiProps) => {
  const selectedDate = date
    ? DateTime.fromFormat(date, "MM-dd-yyyy")
    : DateTime.now();
  const {
    resetAllSelected,
    selectedAddresses,
    moveOrders,
    setRoutes,
    query,
    setRouteLocked,
    setVirtualRoute,
    setAddresses,
    setTransitioningRoutesPhase,
    setDriverMarkers,
    setHasLiveStateChanges,
    setHasPlanningChanges,
    setIsCalculatingEstTime,
    setIsCommittingLiveRoutes,
    initialLiveEstSettlementDate,
    initialLiveEstSettlementClearedAt,
    setInitialLiveEstSettlementDate,
    driverStatusSettlementDate,
    driverStatusSettlementStartedAt,
    driverStatusSettlementClearedAt,
    setDriverStatusSettlementDate,
  } = useRoutePlannerStore(
    (state) => ({
      resetAllSelected: state.resetAllSelected,
      selectedAddresses: state.selectedAddresses,
      moveOrders: state.moveOrders,
      setRoutes: state.setRoutes,
      query: state.query,
      setRouteLocked: state.setRouteLocked,
      setVirtualRoute: state.setVirtualRoute,
      setAddresses: state.setAddresses,
      setTransitioningRoutesPhase: state.setTransitioningRoutesPhase,
      setDriverMarkers: state.setDriverMarkers,
      setHasLiveStateChanges: state.setHasLiveStateChanges,
      setHasPlanningChanges: state.setHasPlanningChanges,
      setIsCalculatingEstTime: state.setIsCalculatingEstTime,
      setIsCommittingLiveRoutes: state.setIsCommittingLiveRoutes,
      initialLiveEstSettlementDate: state.initialLiveEstSettlementDate,
      initialLiveEstSettlementClearedAt:
        state.initialLiveEstSettlementClearedAt,
      setInitialLiveEstSettlementDate: state.setInitialLiveEstSettlementDate,
      driverStatusSettlementDate: state.driverStatusSettlementDate,
      driverStatusSettlementStartedAt: state.driverStatusSettlementStartedAt,
      driverStatusSettlementClearedAt: state.driverStatusSettlementClearedAt,
      setDriverStatusSettlementDate: state.setDriverStatusSettlementDate,
    }),
    shallow,
  );
  const selectedDateKey = selectedDate.toFormat("yyyy-MM-dd");
  const routeStatusPhaseStartedAtRef = useRef(Date.now());
  const previousRouteStatusRef = useRef<RouteStatus | undefined>();
  const initialLiveEstSettlementTimeoutRef = useRef<NodeJS.Timeout | null>(
    null,
  );
  const driverStatusSettlementTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Combined refetch function that refetches both filtered and unfiltered routes
  const refetchAll = async () => {
    await Promise.all([refetch(), refetchUnfilteredRoutes()]);
  };

  // Helper function to handle success cases with toast, state update and refetch
  const createSuccessHandler = (message: string) => () => {
    toast.success(message);
    setHasPlanningChanges(true);
    refetchAll();
  };

  const [queryParams, setQueryParams] = useState<QueryParams>({
    includeLocations: true,
    date: selectedDate.toFormat("yyyy-MM-dd"),
    orderStatuses: [],
    locationTypes: [],
    unassignedActive: false,
    unassignedInactive: false,
  });

  // Keep filtered query date in sync with URL date changes.
  // Without this, first-load redirects (e.g. /route-planner -> /route-planner/:date)
  // can leave queryParams.date stale until a hard refresh.
  useEffect(() => {
    const nextDate = selectedDateKey;
    setQueryParams((prev) =>
      prev.date === nextDate
        ? prev
        : {
            ...prev,
            date: nextDate,
          },
    );
  }, [selectedDateKey]);

  // Fetch unfiltered routes for the table (always shows all routes and locations)
  const {
    isLoading: isLoadingUnfilteredRoutes,
    data: unfilteredRoutesData,
    refetch: refetchUnfilteredRoutes,
  } = useAdminRouteV2ControllerGetRoutesPaginated(
    {
      queryParams: {
        includeLocations: true,
        date: selectedDateKey,
        // No filters applied - table always shows all data
      },
    },
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      staleTime: 0,
    },
  );

  // Only the fields the API reads. With no filters set, this matches the
  // table query's params exactly, so React Query shares one request between
  // them; the unassigned flags drive the separate unassigned-locations query.
  const routesQueryParams = useMemo(
    () => ({
      includeLocations: true,
      date: selectedDateKey,
      ...(queryParams.orderStatuses?.length
        ? { orderStatuses: queryParams.orderStatuses }
        : {}),
      ...(queryParams.locationTypes?.length
        ? { locationTypes: queryParams.locationTypes }
        : {}),
    }),
    [selectedDateKey, queryParams.orderStatuses, queryParams.locationTypes],
  );

  // Fetch filtered routes for the map (respects filter settings)
  const {
    isLoading,
    data: routesData,
    refetch,
  } = useAdminRouteV2ControllerGetRoutesPaginated(
    {
      queryParams: routesQueryParams,
    },
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      staleTime: 0,
    },
  );

  const { currentRouteStatus } = useFilteredRouteOptions(unfilteredRoutesData);

  const {
    isLoading: isLoadingSearchData,
    isFetching: isFetchingSearchData,
    data: searchData,
  } = useAdminLocationV2ControllerGetAllLocations(
    {
      queryParams: {
        search: query,
        limit: 50, // Reasonable limit for search results
      },
    },
    {
      enabled: query.length > 3,
    },
  );

  const { data: liveRouteData, refetch: refetchLiveRouteData } =
    useAdminRouteV2ControllerGetDateOfLiveRoutes({});
  const { data: plannedRouteData, refetch: refetchPlannedRouteData } =
    useAdminRouteV2ControllerGetDateOfBuildRoutes({});

  const { data: driverWsStatus } = useAdminDriverStatusControllerFindAll(
    {},
    { enabled: currentRouteStatus === RouteStatus.Live },
  );

  const {
    data: routeStateDiff,
    isFetching: isFetchingRouteStateDiff,
    dataUpdatedAt: routeStateDiffUpdatedAt,
  } = useAdminRouteV2ControllerGetRouteStateDiff(
    {
      pathParams: {
        date: selectedDateKey,
      },
    },
    {
      enabled:
        currentRouteStatus === RouteStatus.Live ||
        currentRouteStatus === RouteStatus.Planning ||
        currentRouteStatus === RouteStatus.Planned,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      staleTime: 0, // Always consider data stale to ensure fresh checks
    },
  );

  // Determine if we should call the unassigned locations API and with what params
  // Note: When filters change, React Query will automatically refetch because the query key
  // includes the params, which will be different when unassignedApiParams changes
  const shouldCallUnassignedAPI =
    queryParams.unassignedActive || queryParams.unassignedInactive;
  const unassignedApiParams = useMemo(() => {
    if (!shouldCallUnassignedAPI) return null;

    const params: { active?: boolean; type?: "onDemand" | "annual" } = {};

    // Handle active/inactive filter
    // If both are active, don't pass the active param (get all)
    if (queryParams.unassignedActive && !queryParams.unassignedInactive) {
      params.active = true;
    } else if (
      !queryParams.unassignedActive &&
      queryParams.unassignedInactive
    ) {
      params.active = false;
    }
    // If both are true or both are false, don't pass active param

    // Handle type filter (locationTypes)
    // If flatRate is true (has "annual") and onDemand is false (doesn't have "onDemand"), send only "annual"
    // If onDemand is true (has "onDemand") and flatRate is false (doesn't have "annual"), send only "onDemand"
    // In all other cases, don't send the type param at all
    const hasAnnual = queryParams.locationTypes?.includes("annual") ?? false;
    const hasOnDemand =
      queryParams.locationTypes?.includes("onDemand") ?? false;

    if (hasAnnual && !hasOnDemand) {
      params.type = "annual";
    } else if (hasOnDemand && !hasAnnual) {
      params.type = "onDemand";
    }
    // If both or neither are selected, don't include type param

    return params;
  }, [
    queryParams.unassignedActive,
    queryParams.unassignedInactive,
    queryParams.locationTypes,
    shouldCallUnassignedAPI,
  ]);

  const { data: unassignedLocationsData } =
    useAdminLocationV2ControllerGetUnassignedRouteLocationsForDatePaginated(
      {
        pathParams: {
          date: selectedDateKey,
        },
        queryParams: unassignedApiParams || undefined,
      },
      {
        enabled: shouldCallUnassignedAPI && unassignedApiParams !== null,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        staleTime: 0,
      },
    );

  // Frontend validation: filter unassigned data by type
  // Only filter if one type is selected (not both or neither)
  const filteredUnassignedLocationsData = useMemo(() => {
    if (!unassignedLocationsData?.data) return null;

    const locationTypes = queryParams.locationTypes || [];
    const hasAnnual = locationTypes.includes("annual");
    const hasOnDemand = locationTypes.includes("onDemand");

    // Only filter if one is true and the other is false
    // If both are true or both are false, don't filter
    if (hasAnnual === hasOnDemand) {
      // Both true or both false - no filtering
      return unassignedLocationsData.data;
    }

    // Filter based on the selected type
    const targetType = hasAnnual ? "annual" : "onDemand";
    return unassignedLocationsData.data.filter(
      (location) => location.type === targetType,
    );
  }, [unassignedLocationsData, queryParams.locationTypes]);

  useEffect(() => {
    if (
      routesData &&
      routesData?.data &&
      unfilteredRoutesData &&
      unfilteredRoutesData?.data
    ) {
      setRouteLocked(routesData.data[0]?.isLocked ?? false);
      setVirtualRoute(routesData.data[0]?.isVirtual);

      // Use unfiltered routes for the table (so table always shows all routes/locations)
      // The fake routes should only be created in the main component when there are selected unassigned addresses
      // Sort routes by routePriority to ensure visual order matches the priority values
      const sortedRoutes = [...unfilteredRoutesData.data].sort(
        (a, b) => (a.routePriority ?? 0) - (b.routePriority ?? 0),
      );
      setRoutes(sortedRoutes);

      // Create combined markers for the map using FILTERED routes data
      // This way the map respects filter settings while the table shows everything
      const routeMarkers = transformRoutesToMapMarkers(routesData.data);

      // Add unassigned locations as map markers (for map display only, not for table)
      let allMarkers = routeMarkers;
      if (
        filteredUnassignedLocationsData &&
        filteredUnassignedLocationsData.length > 0
      ) {
        const unassignedMarkers = filteredUnassignedLocationsData.map(
          (location) => ({
            id: location.id,
            latitude: location.latitude,
            longitude: location.longitude,
            address: location.address,
            locationSequence: -1, // Use -1 to indicate unassigned locations for icon rendering
            customer: location.customer,
            order: { status: OrderStatuses.Regular },
            duration: location.serviceTime || 0,
            driverStatus: "toBePlowed" as const,
            customerType: location.type || "annual",
            color: "unassigned", // Set marker color to "unassigned" for icon rendering
            // Preserve the actual region for onDemand addresses that don't receive service this shift
            // Only use "Unassigned" as a fallback for truly unassigned addresses
            region: location.region
              ? {
                  id: location.region.id,
                  name: location.region.name,
                  color: "stone",
                }
              : {
                  id: "unassigned",
                  name: "Unassigned",
                  color: "stone", // Use stone color like in region management
                },
            routeId: "unassigned-locations", // Mark as unassigned
            selected: false,
            serviceAccess: location.serviceAccess,
            locationNoteCount: location.locationNoteCount,
          }),
        );

        // Deduplicate markers by ID, giving priority to route markers
        const routeMarkerIds = new Set(
          routeMarkers.map((marker) => marker?.id).filter(Boolean),
        );
        const uniqueUnassignedMarkers = unassignedMarkers.filter(
          (marker) => !routeMarkerIds.has(marker.id),
        );
        allMarkers = [...routeMarkers, ...uniqueUnassignedMarkers];
      }

      setAddresses(allMarkers.filter((marker) => marker !== undefined));
    }
  }, [routesData, unfilteredRoutesData, filteredUnassignedLocationsData]);

  useEffect(() => {
    if (unfilteredRoutesData?.data) {
      const transformedDriverMarkers = transformRoutesToDriverMarkers(
        unfilteredRoutesData.data,
      );

      const driversWithLiveLocations: DriverMarker[] =
        transformedDriverMarkers.map((driverMarker) => {
          const driverInLiveState = driverWsStatus?.data?.find(
            (liveDriver) => liveDriver.driverId === driverMarker.driverId,
          );

          if (!driverInLiveState) {
            return driverMarker;
          }

          const latitude = parseCoordinate(driverInLiveState.latitude);
          const longitude = parseCoordinate(driverInLiveState.longitude);

          if (latitude === undefined || longitude === undefined) {
            return driverMarker;
          }

          return {
            ...driverMarker,
            liveLocation: {
              latitude,
              longitude,
            },
          };
        });

      setDriverMarkers(driversWithLiveLocations);
    }
  }, [unfilteredRoutesData, driverWsStatus]);

  const clearInitialLiveEstSettlement = (dateToClear?: string) => {
    if (
      dateToClear &&
      initialLiveEstSettlementDate &&
      initialLiveEstSettlementDate !== dateToClear
    ) {
      return;
    }

    if (initialLiveEstSettlementTimeoutRef.current) {
      clearTimeout(initialLiveEstSettlementTimeoutRef.current);
      initialLiveEstSettlementTimeoutRef.current = null;
    }

    setInitialLiveEstSettlementDate(undefined);
    setIsCalculatingEstTime(false);
  };

  const startInitialLiveEstSettlement = (dateToSettle: string) => {
    setInitialLiveEstSettlementDate(dateToSettle);
    setIsCalculatingEstTime(true);

    if (initialLiveEstSettlementTimeoutRef.current) {
      clearTimeout(initialLiveEstSettlementTimeoutRef.current);
    }

    initialLiveEstSettlementTimeoutRef.current = setTimeout(() => {
      const { initialLiveEstSettlementDate } = useRoutePlannerStore.getState();
      if (initialLiveEstSettlementDate !== dateToSettle) return;

      clearInitialLiveEstSettlement(dateToSettle);
      queryClient.invalidateQueries({
        queryKey: createRouteStateDiffQueryKey(dateToSettle),
        exact: true,
      });
    }, 30000);
  };

  const clearDriverStatusSettlement = (dateToClear?: string) => {
    const { driverStatusSettlementDate } = useRoutePlannerStore.getState();
    if (
      dateToClear &&
      driverStatusSettlementDate &&
      driverStatusSettlementDate !== dateToClear
    ) {
      return;
    }

    if (driverStatusSettlementTimeoutRef.current) {
      clearTimeout(driverStatusSettlementTimeoutRef.current);
      driverStatusSettlementTimeoutRef.current = null;
    }

    setDriverStatusSettlementDate(undefined);
  };

  // Admin-triggered driver status updates (e.g. Mark as Completed) settle
  // automatically on the backend. During that window the state diff briefly
  // reports changes, which would flash the "Update Routes" button. Suppress
  // those transient reports until the backend settles back to "no changes"
  // (or a safety timeout re-checks the diff).
  const startDriverStatusSettlement = (dateToSettle: string) => {
    setDriverStatusSettlementDate(dateToSettle);

    if (driverStatusSettlementTimeoutRef.current) {
      clearTimeout(driverStatusSettlementTimeoutRef.current);
    }

    driverStatusSettlementTimeoutRef.current = setTimeout(() => {
      const { driverStatusSettlementDate } = useRoutePlannerStore.getState();
      if (driverStatusSettlementDate !== dateToSettle) return;

      clearDriverStatusSettlement(dateToSettle);
      queryClient.invalidateQueries({
        queryKey: createRouteStateDiffQueryKey(dateToSettle),
        exact: true,
      });
    }, 15000);
  };

  useEffect(() => {
    if (previousRouteStatusRef.current === currentRouteStatus) return;

    previousRouteStatusRef.current = currentRouteStatus;
    routeStatusPhaseStartedAtRef.current = Date.now();
    setHasLiveStateChanges(false);
    setHasPlanningChanges(false);
  }, [currentRouteStatus, setHasLiveStateChanges, setHasPlanningChanges]);

  useEffect(() => {
    return () => {
      if (initialLiveEstSettlementTimeoutRef.current) {
        clearTimeout(initialLiveEstSettlementTimeoutRef.current);
      }
      if (driverStatusSettlementTimeoutRef.current) {
        clearTimeout(driverStatusSettlementTimeoutRef.current);
      }
    };
  }, []);

  // Reset state flags when date changes to ensure clean state
  useEffect(() => {
    setHasLiveStateChanges(false);
    setHasPlanningChanges(false);
    clearInitialLiveEstSettlement();
    clearDriverStatusSettlement();
  }, [selectedDateKey]);

  // Check backend state and set button state based on backend response
  useEffect(() => {
    if (!isFetchingRouteStateDiff && routeStateDiff?.data !== undefined) {
      if (routeStateDiffUpdatedAt < routeStatusPhaseStartedAtRef.current) {
        return;
      }

      const hasChanges = routeStateDiff.data.hasChanges;
      const isInitialLiveEstSettling =
        initialLiveEstSettlementDate === selectedDateKey;
      const isDriverStatusSettling =
        driverStatusSettlementDate === selectedDateKey;

      if (currentRouteStatus === RouteStatus.Live) {
        if (
          initialLiveEstSettlementClearedAt &&
          routeStateDiffUpdatedAt < initialLiveEstSettlementClearedAt
        ) {
          return;
        }

        if (
          driverStatusSettlementClearedAt &&
          routeStateDiffUpdatedAt < driverStatusSettlementClearedAt
        ) {
          return;
        }

        if (isDriverStatusSettling) {
          // Only a diff response fetched AFTER the settlement started can end
          // it. The cached pre-update response also says "no changes" and must
          // not be mistaken for the backend having settled.
          if (
            !hasChanges &&
            driverStatusSettlementStartedAt &&
            routeStateDiffUpdatedAt > driverStatusSettlementStartedAt
          ) {
            clearDriverStatusSettlement(selectedDateKey);
          }
          setHasLiveStateChanges(false);
          return;
        }

        setHasLiveStateChanges(isInitialLiveEstSettling ? false : hasChanges);
      } else if (
        currentRouteStatus === RouteStatus.Planning ||
        currentRouteStatus === RouteStatus.Planned
      ) {
        setHasPlanningChanges(hasChanges);
      }
    }
  }, [
    routeStateDiff,
    routeStateDiffUpdatedAt,
    isFetchingRouteStateDiff,
    currentRouteStatus,
    initialLiveEstSettlementDate,
    initialLiveEstSettlementClearedAt,
    driverStatusSettlementDate,
    driverStatusSettlementStartedAt,
    driverStatusSettlementClearedAt,
    selectedDateKey,
  ]);

  const { mutateAsync: updateRouteLock } =
    useAdminRouteV2ControllerUpdateRouteLockStatus({
      onSuccess: () => {
        refetchAll();
      },
    });

  const { mutateAsync: bulkEditStartTime } =
    useAdminRouteV2ControllerUpdateManyRouteStartTime({
      onSuccess: (_, variables) => {
        createSuccessHandler(
          `Start Time for all Routes is set to ${variables.body.startTime}`,
        )();
      },
    });

  const { data: driversData, isLoading: isLoadingDrivers } =
    useDriverControllerGetAll({
      queryParams: { limit: 1000, isAvailable: true, hasVehicle: true }, // Adjust limit as needed
    });

  const { mutate: createRoutes } = useAdminRouteV2ControllerUpdateRoutesState({
    onMutate: ({ pathParams }) => {
      // Optimistically update the query cache
      return updateRouteStatusOptimistically(pathParams.date, "planned");
    },
    onSuccess: () => {
      setTransitioningRoutesPhase({ phase: "build", status: "success" });
      refetchAll();

      // Invalidate the planned date query so initial navigation hook gets fresh data
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v2/admin/routes/build-date",
          operationId: "adminRouteV2ControllerGetDateOfBuildRoutes",
          variables: {},
        }),
      );
    },
    onError: (_, __, context) => {
      setTransitioningRoutesPhase(undefined);

      // Rollback the optimistic update if it failed
      const updateContext = context as OptimisticUpdateContext | undefined;
      if (updateContext) {
        queryClient.setQueryData(
          updateContext.queryKey,
          updateContext.previousData,
        );
      }
    },
  });

  const { mutate: revertToPlanning } =
    useAdminRouteV2ControllerUpdateRouteState({
      onMutate: ({ pathParams }) => {
        // Optimistically update the query cache
        return updateRouteStatusOptimistically(pathParams.date, "planning");
      },
      onSuccess: () => {
        setTransitioningRoutesPhase({ phase: "revert", status: "success" });
        refetchAll();

        // Invalidate planned date query since reverting removes the planned state
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v2/admin/routes/build-date",
            operationId: "adminRouteV2ControllerGetDateOfBuildRoutes",
            variables: {},
          }),
        );
      },
      onError: (_, __, context) => {
        // Rollback the optimistic update if it failed
        const updateContext = context as OptimisticUpdateContext | undefined;
        if (updateContext) {
          queryClient.setQueryData(
            updateContext.queryKey,
            updateContext.previousData,
          );
        }
      },
    });

  const { mutate: reset } =
    useAdminRouteCommitStateControllerResetRouteShiftState({
      onSuccess: () => {
        toast.success("Changes have been reset successfully!");
        refetchAll();
      },
    });

  const { mutate: commit } = useAdminRouteCommitStateControllerCommitLiveRoutes(
    {
      onMutate: () => {
        setIsCommittingLiveRoutes(true);
      },
      onSuccess: () => {
        toast.success("Drivers have been updated successfully!");
        refetchAll();
      },
      onError: () => {
        toast.error("Failed to update drivers. Please try again.");
      },
      onSettled: () => {
        setIsCommittingLiveRoutes(false);
      },
    },
  );

  const { mutate: rollback } =
    useAdminRouteCommitStateControllerRollbackLiveRoutes({
      onSuccess: () => {
        toast.success("Changes have been reset successfully!");
        refetchAll();
      },
    });

  const { mutate: updateRoutePriority } =
    useAdminRouteV2ControllerUpdateRoutePriority({
      onSuccess: () => {
        createSuccessHandler("Route priority updated")();
      },
    });

  const hasRoutesMissingEstimatedTime = unfilteredRoutesData?.data?.some(
    (route) => route.travelDetails?.estimatedTimeMinutes == null,
  );

  const { mutate: dispatchRoutes } =
    useAdminRouteV2ControllerUpdateRouteStateLive({
      onMutate: ({ pathParams }) => {
        setHasPlanningChanges(false);
        setHasLiveStateChanges(false);
        if (
          currentRouteStatus === RouteStatus.Planned &&
          hasRoutesMissingEstimatedTime
        ) {
          startInitialLiveEstSettlement(pathParams.date);
        }
        queryClient.removeQueries({
          queryKey: createRouteStateDiffQueryKey(pathParams.date),
          exact: true,
        });

        // Optimistically update the query cache
        return updateRouteStatusOptimistically(pathParams.date, "live");
      },
      onSuccess: async (_, variables, context) => {
        const started = context as OptimisticUpdateContext | undefined;
        if (started)
          writeLiveShiftDay(
            started.organizationId,
            variables.pathParams.date,
            started.mutationDay,
          );
        setTransitioningRoutesPhase({ phase: "live", status: "success" });
        setHasPlanningChanges(false);
        setHasLiveStateChanges(false);
        await refetchAll();

        queryClient.invalidateQueries({
          queryKey: createRouteStateDiffQueryKey(variables.pathParams.date),
          exact: true,
        });

        // Invalidate live and planned date queries so initial navigation hook gets fresh data
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v2/admin/routes/live-date",
            operationId: "adminRouteV2ControllerGetDateOfLiveRoutes",
            variables: {},
          }),
        );
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v2/admin/routes/build-date",
            operationId: "adminRouteV2ControllerGetDateOfBuildRoutes",
            variables: {},
          }),
        );
      },
      onError: (_, __, context) => {
        setTransitioningRoutesPhase(undefined);
        clearInitialLiveEstSettlement();

        // Rollback the optimistic update if it failed
        const updateContext = context as OptimisticUpdateContext | undefined;
        if (updateContext) {
          queryClient.setQueryData(
            updateContext.queryKey,
            updateContext.previousData,
          );
        }
      },
    });

  const { mutateAsync: updateRoute } = useAdminRouteV2ControllerUpdateRoute({
    onSuccess: (data) => {
      if (data?.data) {
        const { region, driver, availableForAnyDriver, routeName } = data?.data;
        const driverString = driver
          ? driver.name
          : availableForAnyDriver
            ? "Any"
            : "No Driver";
        createSuccessHandler(
          `${driverString} assigned to ${region?.name ?? routeName} Route`,
        )();
      }
    },
  });

  const { mutateAsync: deleteVirtualRoute } =
    useAdminRouteV2ControllerDeleteVirtualRoute({
      onSuccess: () => {
        toast.success("Deleted virtual route");
        refetchAll();
      },
    });

  const { mutateAsync: updateRouteTime } = useAdminRouteV2ControllerUpdateRoute(
    {
      onSuccess: (data) => {
        if (data?.data) {
          const { region, startTime, routeName } = data?.data;
          const startTimeFormatted = formatStartTimeLabel(
            DateTime.fromISO(startTime),
          );
          toast.success(
            `Start Time for ${region?.name ?? routeName} set to ${startTimeFormatted}`,
          );
          refetchAll();
          // Invalidate the route state diff query to re-check if EST needs updating
          // Don't set hasPlanningChanges directly - let the backend check determine this
          queryClient.invalidateQueries({
            queryKey: createRouteStateDiffQueryKey(
              selectedDate.toFormat("yyyy-MM-dd"),
            ),
          });
        }
      },
    },
  );

  const { mutateAsync: updateRouteCustomerOrders } =
    useAdminRouteV2ControllerUpdateRouteCustomerOrders({
      onSuccess: () => {
        createSuccessHandler("Route customer orders updated")();
        resetAllSelected();
      },
    });

  const { mutateAsync: reverseSequence } =
    useAdminRouteV2ControllerReverseSelectedRouteLocationsSequence({
      onSuccess: () => {
        const reversedAddressesCount = selectedAddresses.length;
        createSuccessHandler(
          `Reversed Sequence for ${reversedAddressesCount} Addresses, where ${reversedAddressesCount} is the number of affected addresses.`,
        )();
        resetAllSelected();
      },
    });

  const { mutateAsync: endShift } =
    useAdminRouteV2ControllerUpdateRouteStateEnd({
      onMutate: ({ pathParams }) => {
        // Optimistically update the query cache
        return updateRouteStatusOptimistically(pathParams.date, "ended");
      },
      onSuccess: (_, variables, context) => {
        const parsedDate = DateTime.fromFormat(date as string, "MM-dd-yyyy");
        const formattedDate = parsedDate.toFormat("d MMMM yyyy");
        const endedShiftDate = variables.pathParams.date;
        const ended = context as OptimisticUpdateContext | undefined;
        if (ended) writeLiveShiftDay(ended.organizationId, endedShiftDate);

        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v2/admin/routes/live-date",
            operationId: "adminRouteV2ControllerGetDateOfLiveRoutes",
            variables: {},
          }),
        );

        // Remove all cached queries for the ended shift date to prevent stale data
        // This ensures that if the admin navigates back to this date, fresh data is fetched
        queryClient.removeQueries({
          predicate: (query) => {
            const queryKey = query.queryKey as string[];
            const queryKeyString = JSON.stringify(queryKey);

            // Remove any route-related queries that contain this specific date
            const isRoutesQuery = queryKey.some(
              (part) => typeof part === "string" && part.includes("routes"),
            );
            const containsEndedDate =
              queryKeyString.includes(`"date":"${endedShiftDate}"`) ||
              queryKey.some((part) => part === endedShiftDate);

            return isRoutesQuery && containsEndedDate;
          },
        });

        toast.success(`Ended Shift ${formattedDate}`);
      },
      onError: (_, __, context) => {
        // Rollback the optimistic update if it failed
        const updateContext = context as OptimisticUpdateContext | undefined;
        if (updateContext) {
          queryClient.setQueryData(
            updateContext.queryKey,
            updateContext.previousData,
          );
        }
      },
    });

  const { mutateAsync: transferOrders } =
    useAdminRouteV2ControllerTransferLocationToRoute({
      onSuccess: () => {
        createSuccessHandler(generateToastMessageForTransfer(moveOrders))();
        resetAllSelected();
      },
      onError: () => {
        setRoutes(routesData?.data ?? []);
      },
    });

  const { mutateAsync: updateDriverStatuses } =
    useAdminRouteV2ControllerUpdateRouteDriverStatuses({
      onMutate: () => {
        // Only suppress when there are no pre-existing uncommitted changes,
        // otherwise the already-enabled Update Routes button would flicker off
        const { hasLiveStateChanges } = useRoutePlannerStore.getState();
        if (currentRouteStatus === RouteStatus.Live && !hasLiveStateChanges) {
          startDriverStatusSettlement(selectedDateKey);
        }
      },
      onSuccess: () => {
        createSuccessHandler("Successfully updated driver status!")();
        resetAllSelected();
        queryClient.invalidateQueries(createRoutesInvalidationPredicate());
      },
      onError: () => {
        clearDriverStatusSettlement(selectedDateKey);
      },
    });

  // Route Notification Hooks
  const { data: globalNotificationSettings } =
    useAdminRouteV2ControllerGetRouteNotificationsPerDate({
      pathParams: {
        date: selectedDate.toFormat("yyyy-MM-dd"),
      },
    });

  const { mutateAsync: updateGlobalNotifications } =
    useAdminRouteV2ControllerUpdateRouteNotificationsPerDate({
      onSuccess: (data) => {
        const globalQueryKey = createGlobalRouteNotificationsQueryKey(
          selectedDate.toFormat("yyyy-MM-dd"),
        );

        if (data.data) {
          queryClient.setQueryData<AdminRouteV2ControllerGetRouteNotificationsPerDateResponse>(
            globalQueryKey,
            { data: data.data, statusCode: 200 },
          );
        }

        queryClient.invalidateQueries(createRoutesInvalidationPredicate());
        toast.success("Global notification settings updated successfully!");
      },
    });

  const { mutateAsync: updateRouteNotifications } =
    useAdminRouteV2ControllerUpdateRouteNotifications({
      onSuccess: () => {
        queryClient.invalidateQueries(createRoutesInvalidationPredicate());
        toast.success("Route notification settings updated successfully!");
      },
    });

  const { mutate: triggerEstCalculation } =
    useAdminRouteV2ControllerTriggerEstCalculation({
      onMutate: () => {
        setIsCalculatingEstTime(true);
      },
      onSuccess: () => {
        toast.success("EST calculation triggered successfully!");
        // Note: We don't refetch here as it will be handled by the websocket event
      },
      onError: () => {
        toast.error("Failed to trigger EST calculation");
        setIsCalculatingEstTime(false);
      },
    });

  return {
    routesData,
    unfilteredRoutesData, // Unfiltered routes for the table
    isLoading: isLoading || isLoadingUnfilteredRoutes, // Combined loading state
    refetch: refetchAll, // Use combined refetch function
    refetchLiveRouteData,
    refetchPlannedRouteData,
    liveRouteData,
    plannedRouteData,
    isLoadingSearchData,
    isFetchingSearchData,
    searchData,
    routeStateDiff,
    driversData,
    isLoadingDrivers,
    driverWsStatus,
    updateDriverStatuses,
    transferOrders,
    endShift,
    reverseSequence,
    updateRouteCustomerOrders,
    bulkEditStartTime,
    commit,
    reset,
    createRoutes,
    deleteVirtualRoute,
    dispatchRoutes,
    rollback,
    updateRoute,
    updateRouteTime,
    updateRouteLock,
    updateRoutePriority,
    queryParams,
    setQueryParams,
    revertToPlanning,
    // Notification functions
    globalNotificationSettings,
    updateGlobalNotifications,
    updateRouteNotifications,
    triggerEstCalculation,
  };
};

export default useRoutePlannerApi;
